import React, { useState, useEffect, useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Calendar as CalendarIcon, Clock, X, ChevronDown,
} from 'lucide-react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';

const AVATAR_PALETTE = [
  { bg: '#fdebe6', fg: '#b94f3c' },
  { bg: '#fde6d2', fg: '#F19235' },
  { bg: '#eef5ea', fg: '#53A551' },
  { bg: '#e8eef7', fg: '#4062E5' },
  { bg: '#f5e6f7', fg: '#9333ea' },
];

function avatarColors(key) {
  const str = String(key || '');
  let h = 0;
  // eslint-disable-next-line no-bitwise
  for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTE_STEP = 5;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => String(i * MINUTE_STEP).padStart(2, '0'));

const selectClass = 'appearance-none bg-transparent border-0 text-sm text-ink-900 '
  + 'focus:outline-none tabular-nums font-medium cursor-pointer';

// hh : mm AM/PM selects operating on a Date value
function TimeInput({ value, onChange }) {
  const d = dayjs(value);
  const h24 = d.hour();
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = String((h24 % 12) || 12).padStart(2, '0');
  const minute = String(Math.round(d.minute() / MINUTE_STEP) * MINUTE_STEP).padStart(2, '0');

  const update = (nh12, nm, np) => {
    let h = parseInt(nh12, 10) % 12;
    if (np === 'PM') h += 12;
    onChange(dayjs(value).hour(h).minute(parseInt(nm, 10)).second(0)
      .toDate());
  };

  return (
    <div className="flex items-center gap-1 h-10 px-3 border border-rule rounded-[8px] bg-white">
      <select
        aria-label="Hour"
        value={h12}
        onChange={(e) => update(e.target.value, minute, period)}
        className={selectClass}
        style={{ backgroundImage: 'none', paddingRight: 0 }}
      >
        {HOURS_12.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-ink-900 font-medium">:</span>
      <select
        aria-label="Minute"
        value={minute}
        onChange={(e) => update(h12, e.target.value, period)}
        className={selectClass}
        style={{ backgroundImage: 'none', paddingRight: 0 }}
      >
        {MINUTES.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
      </select>
      <select
        aria-label="Period"
        value={period}
        onChange={(e) => update(h12, minute, e.target.value)}
        className={`${selectClass} font-medium`}
        style={{ backgroundImage: 'none', paddingRight: 0 }}
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
      <Clock size={16} className="text-ink-500 ml-1.5" />
    </div>
  );
}

TimeInput.propTypes = {
  value: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
};

function RangeBar({ fromMin, toMin }) {
  const dayMin = 24 * 60;
  const leftPct = Math.max(0, Math.min(100, (fromMin / dayMin) * 100));
  const widthPct = Math.max(0, Math.min(100 - leftPct, ((toMin - fromMin) / dayMin) * 100));
  const marker = (h) => ({ left: `${(h * 60 * 100) / dayMin}%`, transform: 'translateX(-50%)' });
  return (
    <div className="pt-1 pb-2">
      <div className="flex items-center justify-between text-xs text-ink-500 mb-2">
        <span>Start</span>
        <span>Stop</span>
      </div>
      <div className="relative h-1.5 bg-surface-1 rounded-full">
        <div
          className="absolute h-full bg-[#53A551] rounded-full"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        />
      </div>
      <div className="relative mt-1.5 text-[11px] text-ink-500">
        <span className="absolute" style={marker(6)}>6am</span>
        <span className="absolute" style={marker(12)}>12pm</span>
        <span className="absolute" style={marker(18)}>6pm</span>
        <span className="opacity-0">.</span>
      </div>
    </div>
  );
}

RangeBar.propTypes = {
  fromMin: PropTypes.number.isRequired,
  toMin: PropTypes.number.isRequired,
};

function ManualTimeModal({
  showAddTimeModal,
  setShowAddTimeModal,
  workspaceMember,
  selectedTimezone,
  selectedActivity,
  selectedDate,
  handleAddManualTime,
  projects,
}) {
  const [dateAddTime, setDateAddTime] = useState(dayjs(selectedDate).toDate());
  const [startTime, setStartTime] = useState(dayjs().startOf('hour').toDate());
  const [endTime, setEndTime] = useState(dayjs().startOf('hour').add(1, 'hour').toDate());
  const [reason, setReason] = useState('');
  const [projectId, setProjectId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
  }, [reason, startTime, endTime, projectId]);

  useEffect(() => {
    if (showAddTimeModal) {
      setDateAddTime(dayjs(selectedDate).toDate());
      setStartTime(dayjs().startOf('hour').toDate());
      setEndTime(dayjs().startOf('hour').add(1, 'hour').toDate());
      setReason('');
      setProjectId('');
      setError('');
    }
  }, [showAddTimeModal, selectedDate]);

  // Convert local time to target timezone
  const convertToTimezone = (date, timezoneOffset) => {
    const localOffset = new Date().getTimezoneOffset() * 60000;
    const targetOffset = timezoneOffset * 60000;
    return new Date(date.getTime() + localOffset + targetOffset);
  };

  const getTimezoneOffset = () => {
    const date = new Date();
    try {
      const formattedDate = date.toLocaleString('en-US', { timeZone: selectedTimezone });
      const diff = (date - new Date(formattedDate)) / 60000;
      return Math.round(diff);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Invalid timezone:', selectedTimezone);
      return 0;
    }
  };

  const handleAddTime = async () => {
    setError('');

    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    const timezoneOffset = getTimezoneOffset();
    const dateStr = dayjs(convertToTimezone(dateAddTime, timezoneOffset)).format('YYYY-MM-DD');
    const startTimeStr = dayjs(startTime).format('HH:mm:ss');
    const endTimeStr = dayjs(endTime).format('HH:mm:ss');
    const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';

    const localStart = moment.tz(`${dateStr} ${startTimeStr}`, dateTimeFormat, selectedTimezone);
    const localEnd = moment.tz(`${dateStr} ${endTimeStr}`, dateTimeFormat, selectedTimezone);
    const utcStart = localStart.clone().utc();
    const utcEnd = localEnd.clone().utc();

    if (!utcStart.isBefore(utcEnd)) {
      setError('Start time must be before end time');
      return;
    }

    if (selectedActivity) {
      const hasOverlap = selectedActivity.some((activity) => {
        const activityStart = moment.utc(activity.startDate, 'YYYY-MM-DD HH:mm:ss');
        const activityEnd = activityStart.clone().add(activity.secondsElapsed, 'seconds');
        return utcStart.isBefore(activityEnd) && utcEnd.isAfter(activityStart);
      });
      if (hasOverlap) {
        setError('Selected time overlaps with existing activity');
        return;
      }
    }

    const timeEntry = {
      memberId: workspaceMember?.id,
      startTime: utcStart.format('YYYY-MM-DD HH:mm:ss'),
      endTime: utcEnd.format('YYYY-MM-DD HH:mm:ss'),
      reason: reason.trim(),
      timezone: selectedTimezone,
      duration: utcEnd.diff(utcStart, 'seconds'),
      projectId: projectId || null,
    };

    const res = await sendRequest({
      method: 'post',
      url: API.WORKSPACE.manualActivityEntry(workspaceMember.id),
      data: timeEntry,
    });

    if (res.success === false) {
      setError(res.data.error);
      return;
    }

    setShowAddTimeModal(false);
    handleAddManualTime();
  };

  const firstName = workspaceMember?.user?.firstName;
  const lastName = workspaceMember?.user?.lastName;
  const initials = ((firstName ? firstName[0] : '') + (lastName ? lastName[0] : '')).toUpperCase() || '?';
  const avatar = avatarColors(workspaceMember?.user?.email || workspaceMember?.id);
  const fromMin = useMemo(() => dayjs(startTime).hour() * 60 + dayjs(startTime).minute(), [startTime]);
  const toMin = useMemo(() => dayjs(endTime).hour() * 60 + dayjs(endTime).minute(), [endTime]);

  return (
    <Modal
      open={showAddTimeModal}
      onClose={() => setShowAddTimeModal(false)}
      className="flex-row align-c jstfy-c"
      data-testid="export-all-timesheets-modal"
    >
      <Box style={{
        width: 640,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={() => setShowAddTimeModal(false)}
          className={'absolute top-4 right-4 bg-transparent border-0 p-0 text-ink-500 '
            + 'opacity-70 hover:opacity-100 transition-opacity'}
        >
          <X size={18} />
        </button>

        <h5 className="text-lg font-semibold text-ink-900" data-testid="modal-title">Add time</h5>

        <div className="flex items-center gap-3">
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: avatar.bg, color: avatar.fg }}
          >
            {initials}
          </span>
          <span className="text-ink-900 font-medium">
            {`${firstName ?? ''} ${lastName ?? ''}`}
          </span>
        </div>

        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-ink-500 font-semibold mb-0">
            {`Timezone (${selectedTimezone})`}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div
              aria-disabled="true"
              title="Date is set by the page"
              data-testid="datepicker"
              className={'inline-flex items-center justify-between gap-2 h-10 px-3 border border-rule '
                + 'rounded-[8px] text-sm bg-surface-page text-ink-500 cursor-not-allowed whitespace-nowrap'}
            >
              <span>{moment(dateAddTime).format('ddd, D MMM YYYY')}</span>
              <CalendarIcon size={14} className="text-ink-500/60" />
            </div>
            <div className="flex items-center gap-2 sm:ml-2">
              <span className="text-sm text-ink-900 font-medium">From</span>
              <TimeInput value={startTime} onChange={setStartTime} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-ink-900 font-medium">To</span>
              <TimeInput value={endTime} onChange={setEndTime} />
            </div>
          </div>
        </div>

        <RangeBar fromMin={fromMin} toMin={toMin} />

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="add-time-project" className="block text-sm font-medium text-ink-900 mb-1.5">
            Project (Optional)
          </label>
          <div className="relative">
            <select
              id="add-time-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className={'w-full h-10 pl-3 pr-9 border border-rule rounded-[8px] text-sm bg-white '
                + 'text-ink-900 appearance-none focus:outline-none focus:border-brand-500'}
            >
              <option value="">No Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"
            />
          </div>
        </div>

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="add-time-reason" className="block text-sm font-medium text-ink-900 mb-1.5">Reason</label>
          <textarea
            id="add-time-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className={'w-full px-3 py-2 border border-rule rounded-[8px] text-sm bg-white '
              + 'text-ink-900 focus:outline-none focus:border-brand-500 resize-none'}
          />
        </div>

        {error && (
          <div style={{ color: 'red', textAlign: 'center' }}>
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setShowAddTimeModal(false)}
            data-testid="cancel"
            className={'inline-flex items-center justify-center px-4 py-2 text-sm font-semibold '
              + 'uppercase tracking-wider text-ink-900 hover:text-brand-700 transition'}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddTime}
            data-testid="addTime"
            className={'inline-flex items-center justify-center px-5 py-2 bg-[#53A551] text-white '
              + 'rounded-[8px] text-sm font-semibold uppercase tracking-wider hover:bg-[#458a44] transition'}
          >
            Add time
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ManualTimeModal.propTypes = {
  showAddTimeModal: PropTypes.bool.isRequired,
  setShowAddTimeModal: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  workspaceMember: PropTypes.object.isRequired,
  selectedTimezone: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  selectedActivity: PropTypes.array,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  handleAddManualTime: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  projects: PropTypes.array,
};

ManualTimeModal.defaultProps = {
  selectedActivity: [],
  projects: [],
};

export default ManualTimeModal;
